import type { InputRef } from 'antd';
import {
  Alert,
  Avatar,
  Empty,
  FloatButton,
  Input,
  Segmented,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import { Search, UserRoundSearch, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { alertAttributionApi } from '@/services/alertAttributionApi';
import type {
  AttributionUserLookupReply,
  ResolvedDevice,
} from '@/types/alertAttribution';

type LookupType = 'userId' | 'deviceId';

function formatLookupTime(value?: string) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function deviceName(device: ResolvedDevice) {
  return device.displayName || device.reportedName || '设备名称未提供';
}

export function UserLookupWidget({
  includeSensitive,
}: {
  includeSensitive: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [lookupType, setLookupType] = useState<LookupType>('userId');
  const [lookupValue, setLookupValue] = useState('');
  const [result, setResult] = useState<AttributionUserLookupReply | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<InputRef>(null);
  const requestSequence = useRef(0);
  const sensitiveModeRef = useRef(includeSensitive);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (sensitiveModeRef.current === includeSensitive) return;
    sensitiveModeRef.current = includeSensitive;
    requestSequence.current += 1;
    setResult(null);
    setError('');
    setLoading(false);
  }, [includeSensitive]);

  const changeLookupType = (value: string | number) => {
    requestSequence.current += 1;
    setLookupType(value as LookupType);
    setLookupValue('');
    setResult(null);
    setError('');
    setLoading(false);
  };

  const runLookup = async () => {
    const value = lookupValue.trim();
    if (!value || loading) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const reply = await alertAttributionApi.lookupUser({
        lookupType,
        lookupValue: value,
        includeSensitive,
      });
      if (requestSequence.current === sequence) {
        setResult(reply);
      }
    } catch (lookupError) {
      if (requestSequence.current === sequence) {
        setError(
          lookupError instanceof Error
            ? lookupError.message
            : '用户信息查询失败',
        );
      }
    } finally {
      if (requestSequence.current === sequence) {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {open ? (
        <section
          aria-label="用户与设备查询"
          className="fixed bottom-24 right-6 z-[1000] flex max-h-[min(620px,calc(100vh-120px))] w-[min(400px,calc(100vw-32px))] flex-col overflow-hidden rounded-xl border border-border bg-white shadow-2xl"
        >
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <h2 className="font-semibold">用户 / 设备查询</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                输入 userId 或 deviceId 查询当前信息
              </p>
            </div>
            <button
              aria-label="关闭用户查询"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              type="button"
              onClick={() => setOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <div className="space-y-3 border-b border-border p-4">
            <Segmented
              block
              value={lookupType}
              options={[
                { label: 'User ID', value: 'userId' },
                { label: 'Device ID', value: 'deviceId' },
              ]}
              onChange={changeLookupType}
            />
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                allowClear
                maxLength={256}
                placeholder={
                  lookupType === 'userId' ? '输入 userId' : '输入 deviceId'
                }
                value={lookupValue}
                onChange={(event) => setLookupValue(event.target.value)}
                onPressEnter={() => void runLookup()}
              />
              <Button
                className="shrink-0"
                disabled={!lookupValue.trim() || loading}
                onClick={() => void runLookup()}
              >
                <Search className="mr-1.5 h-4 w-4" />
                查询
              </Button>
            </div>
          </div>

          <div className="min-h-40 flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex min-h-40 items-center justify-center">
                <Spin />
              </div>
            ) : null}
            {!loading && error ? (
              <Alert showIcon type="error" message={error} />
            ) : null}
            {!loading && result?.warnings.length ? (
              <div className="mb-3 space-y-2">
                {result.warnings.map((warning) => (
                  <Alert
                    key={warning}
                    showIcon
                    type="warning"
                    message={warning}
                  />
                ))}
              </div>
            ) : null}
            {!loading && result && result.results.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="未找到匹配用户"
              />
            ) : null}
            {!loading && result?.results.length ? (
              <div className="space-y-3">
                {result.results.map(({ user, devices }) => (
                  <article
                    key={user.userId}
                    className="rounded-lg border border-border p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar
                        className="shrink-0"
                        size={42}
                        src={user.avatar || undefined}
                      >
                        {(user.name || user.userId).slice(0, 1)}
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <Tooltip title={user.name || undefined}>
                          <div className="truncate font-medium">
                            {user.name || '名称未解析'}
                          </div>
                        </Tooltip>
                        <div className="truncate font-mono text-xs text-muted-foreground">
                          {user.userId}
                        </div>
                        {user.email ? (
                          <div className="truncate text-xs text-muted-foreground">
                            {user.email}
                          </div>
                        ) : null}
                      </div>
                      <Tag
                        color={
                          user.resolveStatus === 'RESOLVED' ? 'green' : 'orange'
                        }
                      >
                        {user.resolveStatus === 'RESOLVED'
                          ? '已解析'
                          : user.resolveStatus}
                      </Tag>
                    </div>

                    <div className="mt-3 border-t border-border pt-3">
                      <div className="mb-2 text-xs font-medium text-muted-foreground">
                        {result.lookupType === 'userId'
                          ? `最近设备（${devices.length}）`
                          : `匹配设备（${devices.length}）`}
                      </div>
                      {devices.length === 0 ? (
                        <div className="text-xs text-muted-foreground">
                          暂无设备信息
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {devices.map((device) => (
                            <div
                              key={
                                device.userDeviceId ||
                                `${device.userId}-${device.deviceId}`
                              }
                              className="rounded-md bg-muted/50 p-2 text-xs"
                            >
                              <Tooltip title={deviceName(device)}>
                                <div className="truncate font-medium">
                                  {deviceName(device)}
                                </div>
                              </Tooltip>
                              <div className="mt-0.5 break-all font-mono text-muted-foreground">
                                {device.deviceId}
                              </div>
                              <div className="mt-1 text-muted-foreground">
                                {[
                                  device.platform,
                                  device.arch,
                                  device.clientVersion,
                                ]
                                  .filter(Boolean)
                                  .join(' / ') || '环境信息未提供'}
                              </div>
                              <div className="mt-0.5 text-muted-foreground">
                                最近活跃：{formatLookupTime(device.lastSeenAt)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
            {!loading && !result && !error ? (
              <div className="flex min-h-40 flex-col items-center justify-center text-sm text-muted-foreground">
                <UserRoundSearch className="mb-2 h-8 w-8" />
                选择查询类型并输入 ID
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
      <FloatButton
        aria-label={open ? '收起用户查询' : '打开用户查询'}
        icon={<UserRoundSearch className="h-5 w-5" />}
        tooltip={open ? '收起用户查询' : '查询用户 / 设备'}
        type="primary"
        style={{ right: 24, bottom: 24 }}
        onClick={() => setOpen((current) => !current)}
      />
    </>
  );
}
